import { createFileRoute } from '@tanstack/react-router';
import { PageHero } from '../components/ui/hero.js';
import { BlogScene } from '../components/mascot/scenes.js';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';
import {
  authorFirstName,
  fetchPosts,
  formatPostDate,
  type Post,
} from '../content/posts.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { SiteLink } from '../components/ui/button.js';

/**
 * The blog index.
 *
 * ===========================================================================
 * One featured post, then a grid
 * ===========================================================================
 * The outgoing blog was a uniform three-column grid of cards, which gives the
 * newest post exactly as much weight as one from two years ago and leaves the
 * page with no entry point. Editorial pages almost always lead with one piece
 * and list the rest, because that is the shape that answers "where do I start".
 *
 * So the newest post gets a wide split — image beside the words, at a size the
 * cover image can actually be read at — and everything after it is a card. Same
 * content, an order of importance added.
 *
 * A typographic contents page was tried instead of the grid — rows with the
 * date in a left column, the headline set large, a small still on the right —
 * and it was reverted. It read well and it read like a *magazine*, which is a
 * different promise from the one this page makes: these are how-to guides
 * somebody arrives on from a search, and a card carrying its cover is easier to
 * pick out of twelve than a line of text is. The note is here so the idea is
 * not rediscovered and re-shipped a third time.
 *
 * ===========================================================================
 * Loaded on the server
 * ===========================================================================
 * Posts are in the HTML a crawler receives. A blog that assembles itself after
 * hydration is a blog that, as far as search is concerned, has no articles in
 * it — which for the twelve indexed URLs this page has to keep alive would be
 * the whole point thrown away.
 */
export const Route = createFileRoute('/blog/')({
  head: () => {
    const { meta, links } = seo({
      title: 'Review management tips and guides',
      description:
        'How to collect reviews, choose what to publish, and show them on your website — written for the people who actually have to do it.',
      path: '/blog',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Blog', path: '/blog' },
            ])
          ),
        },
      ],
    };
  },
  loader: () => fetchPosts(),
  component: BlogIndex,
});

function BlogIndex() {
  const { posts, ok, unconfigured } = Route.useLoaderData();
  const [featured, ...rest] = posts;

  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
        ]}
        art={<BlogScene />}
      >
        <Measure>
          <Eyebrow>Blog</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            Getting reviews to <span className="whitespace-nowrap"><Mark>do some work</Mark>.</span>
          </Heading>
          <Text size="lead" className="mt-5">
            How to collect them, choose what to publish, and put them where they
            change somebody&rsquo;s mind — written for the people who actually
            have to do it.
          </Text>
        </Measure>
      </PageHero>

      {posts.length === 0 ? (
        <Section>
          <EmptyState ok={ok} unconfigured={unconfigured} />
        </Section>
      ) : (
        <>
          <Section size="tight" className="pt-10">
            {featured ? <FeaturedPost post={featured} /> : null}
          </Section>

          {rest.length > 0 ? (
            <Section tone="raised" aria-labelledby="more-heading">
              <h2 id="more-heading" className="sr-only">
                More articles
              </h2>
              <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((post) => (
                  <li key={post.slug}>
                    <PostCard post={post} />
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </>
      )}
    </>
  );
}

/**
 * The newest post, given room.
 *
 * `items-center` rather than a stretched image: the words are the thing being
 * chosen between, and a cover cropped to the exact height of a three-line
 * paragraph reads as a banner rather than as a picture belonging to an article.
 */
function FeaturedPost({ post }: { post: Post }) {
  const date = formatPostDate(post.date);

  return (
    <article className="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:items-center lg:gap-14">
      {post.cover ? (
        <SiteLink
          to={`/blog/${post.slug}`}
          className="group block overflow-hidden rounded-2xl bg-ink-25 ring-1 ring-ink-150"
          tabIndex={-1}
          aria-hidden
        >
          <img
            src={post.cover.url}
            alt=""
            width={post.cover.width}
            height={post.cover.height}
            /*
              The one image on this page worth loading eagerly — it is the
              largest thing above the fold. Everything below is lazy.
            */
            loading="eager"
            fetchPriority="high"
            className="aspect-[16/10] w-full object-cover transition-transform duration-[var(--duration-base)] [transition-timing-function:var(--ease-out-soft)] group-hover:scale-[1.02]"
          />
        </SiteLink>
      ) : null}

      <div>
        <PostDate date={date} />
        <h2 className="mt-4 text-display-sm text-balance text-ink-950 sm:text-display">
          <SiteLink
            to={`/blog/${post.slug}`}
            className="rounded-sm underline-offset-4 hover:underline"
          >
            {post.title}
          </SiteLink>
        </h2>
        {post.excerpt ? (
          <p className="mt-4 max-w-prose text-body-lg text-ink-600">
            {post.excerpt}
          </p>
        ) : null}
        {post.author ? (
          <p className="mt-6 text-body-sm text-ink-500">
            By <span className="font-medium text-ink-800">{authorFirstName(post.author.name)}</span>
          </p>
        ) : null}
      </div>
    </article>
  );
}

function PostCard({ post }: { post: Post }) {
  const date = formatPostDate(post.date);

  return (
    <article
      className={[
        'group flex h-full flex-col overflow-hidden rounded-xl bg-white ring-1 ring-ink-150',
        'transition-shadow duration-[var(--duration-base)] hover:shadow-md',
      ].join(' ')}
    >
      {post.cover ? (
        <img
          src={post.cover.url}
          alt=""
          width={post.cover.width}
          height={post.cover.height}
          loading="lazy"
          decoding="async"
          className="aspect-[16/10] w-full object-cover"
        />
      ) : (
        // No cover is a normal state, not a broken one. A tinted panel keeps the
        // card the same height as its neighbours instead of leaving one short.
        <div className="aspect-[16/10] w-full bg-ink-25" aria-hidden />
      )}

      <div className="flex flex-1 flex-col p-5">
        <PostDate date={date} />
        <h3 className="mt-3 text-title text-ink-950">
          <SiteLink
            to={`/blog/${post.slug}`}
            className="rounded-sm underline-offset-4 group-hover:underline"
          >
            {post.title}
          </SiteLink>
        </h3>
        {post.excerpt ? (
          <p className="mt-2.5 line-clamp-3 text-body-sm text-ink-600">
            {post.excerpt}
          </p>
        ) : null}
      </div>
    </article>
  );
}

function PostDate({ date }: { date: string | null }) {
  if (!date) return null;

  return (
    <p className="text-caption text-ink-500">
      <time>{date}</time>
    </p>
  );
}

/**
 * What the page says when there is nothing to list.
 *
 * Three states, and they are not the same thing: the CMS is not configured
 * (a deployment gap), the CMS could not be reached (an outage), or it genuinely
 * has no published posts. Collapsing them into one "no posts" message is how a
 * broken integration goes unnoticed.
 */
function EmptyState({ ok, unconfigured }: { ok: boolean; unconfigured: boolean }) {
  const [title, body] = unconfigured
    ? [
        'The blog is not connected yet.',
        'This deployment has no PAYLOAD_SECRET set, so Payload cannot start and there is nothing to read posts from.',
      ]
    : !ok
      ? [
          'Articles could not be loaded.',
          'The CMS did not answer. The posts are still there — please try again shortly.',
        ]
      : [
          'Nothing published yet.',
          'The first articles are being written. Check back soon.',
        ];

  return (
    <Measure>
      <div
        role="status"
        className="rounded-xl bg-ink-25 px-6 py-10 text-center ring-1 ring-ink-150"
      >
        <p className="text-title text-ink-950">{title}</p>
        <p className="mx-auto mt-2 max-w-prose text-body-sm text-ink-600">
          {body}
        </p>
      </div>
    </Measure>
  );
}
